import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ContentList, ContentPanel, Grid, Modal } from '@hopskipdrive/react-components';
import { DROPOFF, PICKUP } from '@rideiq/constants';
import { useTimezone } from '@rideiq/hooks/useTimezone';
import { useRideEditor } from '@rideiq/rides/hooks/useRideEditor';
import { Button } from '@rideiq/shared/components/Button';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';
import { useToastStore } from '@rideiq/shared/stores/toastStore';
import { isPast } from 'date-fns';
import { countBy, includes, isEmpty } from 'lodash';

import { DateSection } from '../../components/DateSection';
import { RideTags } from '../../components/RideTags';
import { getDirtyFieldsForSeries, getDirtyFieldsForSingleRide } from '../../pages/EditRideDetails/getDirtyFields';
import CantRideAloneModal from '../CantRideAloneModal';
import { RiderAssistantAdvisoryModal } from '../CantRideAloneModal/RiderAssistantAdvisoryModal';
import { useRiderAgeIntegrityChecker } from '../CantRideAloneModal/useRiderAgeIntegrityChecker';
import { DashcamNotificationModal, useValidateDashcamForMixedRiders } from '../DashcamNotificationModal';
import { Waypoints } from '../Waypoints';
import { getWaypointFormValues } from '../Waypoints/helpers';

import { GenderPreferenceModal } from './GenderPreferenceModal';
import { OffHoursModal } from './OffHoursModal';
import { rideSchema } from './validationSchema';
import { withDateInputValidation } from './withDateInputValidation';

interface RideFormProps {
  rideDetails: RideFormType;
  onSubmit: (data: Partial<RideFormData>) => void;
  isSeries: boolean;
  onFormValues?: (data: Partial<RideFormData>) => void;
  isNewRide?: boolean;
}

const Presentation = ({ onSubmit, rideDetails, isSeries, onFormValues, isNewRide = true }: RideFormProps, ref) => {
  const { getRiderOptions, completeAnchorTime, validatedWaypoints, waypointsOnMap, tripTemplateForSmartPooling } =
    useRideEditor();
  const { ridersOnRide } = getRiderOptions();
  const { addToast, resetToasts } = useToastStore();
  const { getDateInTimezone, mergeDateTime } = useTimezone();
  const dateInTimezone = getDateInTimezone();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOffHoursModalOpen, setIsOffHoursModalOpen] = useState(false);
  const [openNoValuesSubmittedModal, setOpenNoValuesSubmittedModal] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Partial<RideFormData>>({});
  const { openAgeWarningModal, setAgeWarningModal, openAgeAdvisoryModal, setAgeAdvisoryModal, checkAgeIntegrity } =
    useRiderAgeIntegrityChecker();

  const nextAvailableDate = dateInTimezone;
  const schema = rideSchema(
    isSeries,
    nextAvailableDate,
    getDateInTimezone(),
    rideDetails.tripTemplateAttributes?.startDate ? isPast(rideDetails.tripTemplateAttributes?.startDate) : false
  );
  const defaultState = useMemo(() => {
    return {
      ...rideDetails,
      tripTemplateAttributes: rideDetails.tripTemplateAttributes,
      timeAnchor: rideDetails.timeAnchor || DROPOFF,
    };
  }, [rideDetails]);

  const { handleSubmit, getValues, getFieldState, setValue, register, control, watch, reset, formState, ...rest } =
    useForm<RideFormData>({
      // Initialize from the source ride while keeping defaultState as the blank route-reset template.
      defaultValues: useMemo(() => {
        return {
          ...defaultState,
          waypointsAttributes: rideDetails.waypoints?.length
            ? getWaypointFormValues(rideDetails.waypoints)
            : defaultState.waypointsAttributes,
        };
      }, [defaultState, rideDetails.waypoints]),
      resolver: withDateInputValidation(yupResolver(schema), () => getFieldState('anchorTime').error),
      shouldUnregister: true,
    });

  const { openDashcamNotificationModal, hasDashcamConflicts, closeDashcamNotificationModal } =
    useValidateDashcamForMixedRiders();

  const hasValidRiders = (rideData) => {
    const { waypointsAttributes, timeAnchor } = rideData;
    const riderSlugs = waypointsAttributes
      .filter((waypoint) => !waypoint.anchor)
      .flatMap((waypoint) => waypoint.passengerIds);
    const riderNames = ridersOnRide.filter((rider) => !includes(riderSlugs, rider.id)).map((rider) => rider.name);

    if (riderNames?.length) {
      resetToasts();
      addToast({
        severity: 'error',
        message: (
          <>
            <p>{'Oops! We were unable to create this trip due to the following issues:'}</p>
            {riderNames.map((riderName, index) => {
              return (
                <p key={`error-message-${index}`}>{`• ${riderName} has not been added to a ${(timeAnchor === PICKUP
                  ? DROPOFF
                  : PICKUP
                ).replace('_', '-')} location`}</p>
              );
            })}
            <p>{'Please make the appropriate changes and try saving again.'}</p>
          </>
        ),
      });

      return false;
    }
    const prevRidersSlugs =
      rideDetails.waypoints?.flatMap((waypoint) => waypoint.riders).map((rider) => rider.slug) || [];
    const prevRiders = ridersOnRide.filter((rider) => includes(prevRidersSlugs, rider.id));
    const newRiders = ridersOnRide.filter((rider) => !includes(prevRidersSlugs, rider.id));

    // only check gender preferences for multi-rider rides
    // checking for newRiders length makes it work for both create/edit
    if (ridersOnRide.length > 1 && newRiders.length && hasConflictingGenderPreferences(prevRiders, newRiders)) {
      setIsModalOpen(true);

      return false;
    }

    if (hasDashcamConflicts(ridersOnRide)) {
      return false;
    }

    // show the off hours modal if no gender conflicts
    const showConfirmOffHoursModal = showConfirmOffHours(parseInt(rideData.time));

    setIsOffHoursModalOpen(showConfirmOffHoursModal);

    return true;
  };

  const finishSubmit = (data: RideFormData) => {
    const changedFields = setChangedFields(
      validatedWaypoints.length ? { ...data, waypointsAttributes: validatedWaypoints } : data
    );

    if (!isEmpty(changedFields)) {
      const showConfirmOffHoursModal = showConfirmOffHours(parseInt(data.time));

      hasValidRiders(data) && !showConfirmOffHoursModal && onSubmit(changedFields);
    }
  };

  const setChangedFields = useCallback(
    (data) => {
      const fields = isNewRide ? data : getDirtyFields(data, rideDetails, isSeries);

      if (isEmpty(fields)) {
        setDirtyFields(fields);
        setOpenNoValuesSubmittedModal(true);

        return {};
      }

      const newRideAnchorTime = fields.anchorTime;
      const editRideAnchorTime =
        fields.time && fields.anchorTime ? mergeDateTime(fields.anchorTime, fields.time) : null;

      const anchorTime = isNewRide ? newRideAnchorTime : editRideAnchorTime;

      const changedFields = {
        ...fields,
        ...(anchorTime && {
          anchorTime,
        }),
      };

      setDirtyFields(changedFields);

      return changedFields;
    },
    [rideDetails, isSeries]
  );

  useEffect(() => {
    const formValues = getValues();

    onFormValues &&
      onFormValues({
        ...formValues,
        tripTemplateAttributes: tripTemplateForSmartPooling,
        waypointsAttributes: formValues.waypointsAttributes?.map((waypoint, index) => ({
          ...waypoint,
          anchor: waypointsOnMap[index]?.anchor,
          latLng: waypointsOnMap[index]?.latLng,
        })),
      });
  }, [completeAnchorTime, ridersOnRide, waypointsOnMap, waypointsOnMap[0].anchor, tripTemplateForSmartPooling]);

  const myFormKeyMapper = {
    passengerIds: 'Rider',
    id: null,
    placeId: null,
  };

  const errors = useMemo(() => formState.errors, [formState.errors]);

  return (
    <FormProvider
      {...{ handleSubmit, getValues, getFieldState, setValue, register, control, watch, reset, formState, ...rest }}
    >
      <FormA11yErrorAnnouncer errors={errors} keyMapper={myFormKeyMapper} />
      <form
        ref={ref}
        id="rides-form"
        aria-label="form"
        onSubmit={handleSubmit((data) => {
          if (!checkAgeIntegrity(validatedWaypoints, ridersOnRide)) {
            return;
          }

          finishSubmit(data);
        })}
      >
        <DateSection
          isSeries={isSeries}
          nextAvailableDate={nextAvailableDate}
          rideDetailsDefault={defaultState}
          editMode={true}
        />
        <ContentPanel mb={4}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={12} lg={12}>
              <Waypoints rideDetailsDefault={defaultState} isSeries={isSeries} />
            </Grid>
          </Grid>
        </ContentPanel>
        {
          <ContentPanel>
            <Grid item xs={12} md={12} lg={10}>
              <ContentList>
                <RideTags />
              </ContentList>
            </Grid>
          </ContentPanel>
        }
      </form>
      {isModalOpen ? (
        <GenderPreferenceModal
          isOpen={isModalOpen}
          riders={ridersOnRide.filter((rider) => rider.driverGenderPreferences?.length)}
          handleSave={handleSubmit((data) => {
            const showConfirmOffHoursModal = showConfirmOffHours(parseInt(data.time));

            !showConfirmOffHoursModal && onSubmit(dirtyFields);
            setIsModalOpen(false);
            setIsOffHoursModalOpen(showConfirmOffHoursModal);
          })}
          handleClose={() => setIsModalOpen(false)}
        />
      ) : null}
      <OffHoursModal
        anchorTime={completeAnchorTime}
        isOpen={isOffHoursModalOpen}
        handleSave={() => {
          onSubmit(dirtyFields);
          setIsOffHoursModalOpen(false);
        }}
        handleClose={() => setIsOffHoursModalOpen(false)}
      />
      {openNoValuesSubmittedModal && (
        <NoChangedValueModal
          isOpen={openNoValuesSubmittedModal}
          handleClose={() => setOpenNoValuesSubmittedModal(false)}
        />
      )}
      <CantRideAloneModal isOpen={openAgeWarningModal} close={() => setAgeWarningModal(false)} />
      <RiderAssistantAdvisoryModal
        isOpen={openAgeAdvisoryModal}
        handleClose={() => setAgeAdvisoryModal(false)}
        handleContinue={handleSubmit((data) => {
          setAgeAdvisoryModal(false);
          finishSubmit(data);
        })}
      />
      <DashcamNotificationModal
        isOpen={openDashcamNotificationModal}
        handleSave={handleSubmit((data) => {
          const showConfirmOffHoursModal = showConfirmOffHours(parseInt(data.time));

          !showConfirmOffHoursModal && onSubmit(dirtyFields);
          closeDashcamNotificationModal();
          setIsOffHoursModalOpen(showConfirmOffHoursModal);
        })}
        handleClose={closeDashcamNotificationModal}
      />
    </FormProvider>
  );
};

export const RideForm = forwardRef(Presentation);

export const showConfirmOffHours = (selectedHour: number) => {
  const MIN_OFF_HOURS = 5;
  const MAX_OFF_HOURS = 17;

  return selectedHour <= MIN_OFF_HOURS || selectedHour >= MAX_OFF_HOURS;
};

function hasConflictingGenderPreferences(prevRiders, newRiders) {
  const ridersWithPref = [...prevRiders, ...newRiders].filter((rider) => rider.driverGenderPreferences?.length);

  if (ridersWithPref.length <= 1) {
    return false;
  }

  const occurrencesMap = countBy(ridersWithPref.map((rider) => rider.driverGenderPreferences).flat());
  const occurrences = Object.values(occurrencesMap);

  // Check if all occurrences are equal (if not, it means that some rider has different gender preference)
  const hasOccurrencesDifference = !occurrences.every((value) => value === occurrences[0]);
  // If the number of occurrences is equal, we still need to check if number of occurrences
  // are equals to the number of riders with gender pref, to cover the case of, lets say,
  // 2 riders with "male" pref and 2 riders with "female", occurrences are equals, but it has preferences difference
  const hasGenderPreferenceDifference = !hasOccurrencesDifference && !(ridersWithPref.length === occurrences[0]);

  return hasOccurrencesDifference || hasGenderPreferenceDifference;
}

const NoChangedValueModal = ({ isOpen, handleClose }: { isOpen: boolean; handleClose: () => void }) => (
  <Modal open={isOpen} onClose={handleClose} data-testid="no-values-changed-modal" maxWidth="sm">
    <Modal.Title>{'No Changes'}</Modal.Title>
    <Modal.Content>
      {
        'No changes were made to the ride. After making changes to the ride, select Save. Otherwise, click the cancel button.'
      }
    </Modal.Content>
    <Modal.Actions>
      <Button autoFocus color="primary" variant="contained" onClick={handleClose}>
        Ok
      </Button>
    </Modal.Actions>
  </Modal>
);

function getDirtyFields(data: RideFormData, memoizedRideDetails: RideFormType, isSeries: boolean) {
  const anchorTime = data.anchorTime || memoizedRideDetails.anchorTime;

  const updatedRide = {
    ...data,
    ...(anchorTime && {
      anchorTime: anchorTime,
    }),
  };

  const originalRide = {
    anchorTime: memoizedRideDetails.anchorTime,
    timeAnchor: memoizedRideDetails.timeAnchor,
    time: memoizedRideDetails.time,
    calendarSlug: memoizedRideDetails.calendarSlug,
    tagList: memoizedRideDetails.tagList,
    waypointsAttributes:
      memoizedRideDetails.waypoints?.map((wp) => ({
        ...wp,
        anchor: !!wp.anchor,
        passengerIds: wp.riders.map(({ slug }) => slug),
      })) || [],
  };

  return isSeries
    ? getDirtyFieldsForSeries(
        {
          ...originalRide,
          tripTemplateAttributes: memoizedRideDetails.tripTemplateAttributes,
        },
        { ...updatedRide, tripTemplateAttributes: updatedRide.tripTemplateAttributes }
      )
    : getDirtyFieldsForSingleRide(originalRide, updatedRide);
}
